import React, { useRef, useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  makeStyles,
  TextField,
  useTheme,
  useMediaQuery,
  Drawer
} from '@material-ui/core';
import { Close } from '@carbon/icons-react';
import {
  CapaErrorCode,
  useUpdateBypassChatComplaintMutation
} from '@generated/graphql';
import errorMessage from '@utils/errorMessage';
import { colorSet, useSnackbar } from '@components/Provider';
import { CustomizedButton, CustomizedTypography } from '@components/Customized';
import { BypassDialogState } from './ChatBubble/MessageBubble/BypassWarningBubble';

interface BypassDialogProps {
  onClose: () => void;
  dialogState: BypassDialogState;
}

const BypassDialog: React.FunctionComponent<BypassDialogProps> = ({
  onClose,
  dialogState: { open, type, targetMessageId }
}) => {
  const [isValid, setIsValid] = useState<boolean>(false);
  const complaint = useRef<string>('');
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('xs'));
  const { paper, drawerPaper, title, content, actions } = useStyles();
  const [updateBypassComplaint] = useUpdateBypassChatComplaintMutation();
  const { enqueueSnackbar } = useSnackbar();

  const onSubmit = async () => {
    if (!type || !complaint.current || !targetMessageId) return;

    try {
      const result = await updateBypassComplaint({
        variables: {
          input: {
            chatMessageId: targetMessageId,
            complaint: complaint.current,
            complaintType: type
          }
        }
      });

      if (
        result.data?.updateBypassChatComplaint?.result !== CapaErrorCode.Success
      ) {
        throw new Error(
          'update bypass complaint failed with ' +
            result.data?.updateBypassChatComplaint.result
        );
      }
      enqueueSnackbar('불편사항을 접수하였습니다.');
      onClose();
    } catch (e) {
      enqueueSnackbar(errorMessage.updateBypassComplaint);
    }
  };

  const dialogContent = (
    <>
      <DialogTitle className={title} disableTypography>
        <CustomizedTypography variant="h4" fontWeight="700">
          불편사항 접수
        </CustomizedTypography>
        <IconButton className="close-button" onClick={onClose}>
          <Close size={24} color={colorSet.gray900} />
        </IconButton>
      </DialogTitle>

      <DialogContent className={content}>
        <CustomizedTypography
          variant="subtitle1"
          color="textPrimary"
          gutterBottoms={4}
        >
          캐파는 거래의 안전을 위해 외부거래를 지양합니다.
        </CustomizedTypography>
        <CustomizedTypography
          variant="body1"
          color="textSecondary"
          gutterBottoms={16}
        >
          별도 채널로 소통하는 이유를 적어주시면 반영하여 개선하겠습니다.
        </CustomizedTypography>
        <TextField
          className="complaint-input"
          multiline
          placeholder="불편사항을 적어주세요."
          fullWidth
          variant="outlined"
          minRows={4}
          maxRows={4}
          onChange={(e) => {
            const value = e.target.value;

            if (value) {
              setIsValid(true);
            } else {
              setIsValid(false);
            }

            complaint.current = value;
          }}
        />
      </DialogContent>

      <DialogActions className={actions}>
        <CustomizedButton
          disabled={!isValid}
          color="primary"
          variant="contained"
          fullWidth
          size="large"
          onClick={onSubmit}
        >
          보내기
        </CustomizedButton>
      </DialogActions>
    </>
  );

  if (matches)
    return (
      <Drawer
        classes={{ paper: drawerPaper }}
        open={open}
        onClose={onClose}
        anchor="bottom"
      >
        {dialogContent}
      </Drawer>
    );

  return (
    <Dialog classes={{ paper }} open={open} onClose={onClose}>
      {dialogContent}
    </Dialog>
  );
};

export default BypassDialog;

const useStyles = makeStyles((theme) => ({
  paper: {
    maxWidth: '476px',
    width: '100%',
    borderRadius: '16px'
  },
  drawerPaper: {
    borderRadius: '16px 16px 0px 0px'
  },
  title: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    height: '88px',
    '& .close-button': {
      position: 'absolute',
      top: '18px',
      right: '18px',
      padding: '6px'
    },
    [theme.breakpoints.down('xs')]: {
      padding: '16px 20px'
    }
  },
  content: {
    padding: '0px 24px 24px',
    '& p, h6': {
      wordBreak: 'break-all'
    },
    [theme.breakpoints.down('xs')]: {
      padding: '0px 16px 24px'
    }
  },
  actions: {
    borderTop: `1px solid ${colorSet.gray400}`,
    padding: '16px 24px',
    [theme.breakpoints.down('md')]: {
      padding: '16px'
    }
  }
}));
